import React from 'react';
import { Link } from 'react-router-dom';

/**
 * RewordGenie brand mark — the "loop + spark" logo.
 *
 * Geometry matches design/logos/brand/logo-primary.svg exactly (see
 * design/logos/brand/DESIGN_RULES.md). The loop is an open arc with the gap at
 * ~1 o'clock; the spark is centered in the loop.
 *
 * Props:
 *   size      - pixel size of the square mark (default 36)
 *   variant   - 'gradient' (emerald, default) | 'mono' (inherits currentColor)
 *   tile      - if true, render on a rounded gradient tile (app-icon style)
 */
export function LogoMark({ size = 36, variant = 'gradient', tile = false, className = '' }) {
  const gid = React.useId();
  const loopStroke = variant === 'mono' ? 'currentColor' : `url(#rg-grad-${gid})`;
  const sparkFill = variant === 'mono' ? 'currentColor' : (tile ? '#FBBF24' : '#F59E0B');

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 160 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="RewordGenie"
      className={className}
    >
      <defs>
        <linearGradient id={`rg-grad-${gid}`} x1="20" y1="20" x2="140" y2="140" gradientUnits="userSpaceOnUse">
          <stop stopColor="#059669" />
          <stop offset="1" stopColor="#34D399" />
        </linearGradient>
      </defs>
      {tile && <rect width="160" height="160" rx="40" fill={`url(#rg-grad-${gid})`} />}
      <path
        d="M118.218 62.583 A42 42 0 1 1 87.654 38.703"
        stroke={tile ? '#FFFFFF' : loopStroke}
        strokeWidth="14"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M80 59 L87.5 72.5 L101 80 L87.5 87.5 L80 101 L72.5 87.5 L59 80 L72.5 72.5 Z"
        fill={tile ? '#FFFFFF' : sparkFill}
      />
    </svg>
  );
}

/**
 * RewordGenie wordmark: the mark + "Reword" (gradient) + "Genie" (ink).
 *
 * Props:
 *   size      - mark size in px (default 36); text scales with it
 *   to        - if set, wraps the logo in a Link to this route
 *   onClick   - optional click handler (e.g. analytics)
 *   className - extra classes on the wrapper
 */
export default function Logo({ size = 36, to, onClick, className = '' }) {
  const content = (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <LogoMark size={size} />
      <span
        className="font-extrabold tracking-tight"
        style={{ fontSize: size * 0.62 }}
      >
        <span className="gradient-text">Reword</span>
        <span className="text-text-primary">Genie</span>
      </span>
    </span>
  );

  if (to) {
    return (
      <Link to={to} onClick={onClick} className="inline-flex items-center group">
        {content}
      </Link>
    );
  }
  return content;
}
